import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Wallet, Plus, Trash2, TrendingDown, Calendar, Tag, DollarSign,
  Loader2, AlertCircle, ArrowUpRight, ArrowDownRight, X
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from './adminConstants';

const CATEGORY_LABELS = {
  trafego_pago: { label: 'Tráfego Pago', icon: '📢', color: 'bg-purple-100 text-purple-700' },
  marketing: { label: 'Materiais de Marketing', icon: '🎬', color: 'bg-fuchsia-100 text-fuchsia-700' },
  comissoes: { label: 'Comissões de Venda', icon: '🤝', color: 'bg-orange-100 text-orange-700' },
  plataforma: { label: 'Plataforma Emergent', icon: '🛠️', color: 'bg-blue-100 text-blue-700' },
  llm: { label: 'LLM / IA', icon: '🤖', color: 'bg-cyan-100 text-cyan-700' },
  dominio: { label: 'Domínio', icon: '🌐', color: 'bg-emerald-100 text-emerald-700' },
  whatsapp: { label: 'WhatsApp (Z-API)', icon: '💬', color: 'bg-green-100 text-green-700' },
  email: { label: 'Email (Resend)', icon: '✉️', color: 'bg-amber-100 text-amber-700' },
  pagamentos: { label: 'Pagamentos (Asaas)', icon: '💳', color: 'bg-indigo-100 text-indigo-700' },
  design: { label: 'Design', icon: '🎨', color: 'bg-pink-100 text-pink-700' },
  consultoria: { label: 'Consultoria', icon: '👨‍💼', color: 'bg-rose-100 text-rose-700' },
  outros: { label: 'Outros', icon: '📦', color: 'bg-slate-100 text-slate-700' },
};

const formatBRL = (v) => `R$ ${(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const formatMonths = (v) => `${(v || 0).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} ${v === 1 ? 'mês' : 'meses'}`;

const AdminCostsTab = () => {
  const { getToken } = useAuth();
  const [costs, setCosts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    category: 'trafego_pago',
    description: '',
    amount: '',
    date: new Date().toISOString().slice(0, 10),
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const token = getToken();
      const [r1, r2] = await Promise.all([
        axios.get(`${API}/admin/costs`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/costs/summary`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setCosts(r1.data || []);
      setSummary(r2.data);
    } catch {
      toast.error('Erro ao carregar custos');
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.description.trim() || !form.amount) {
      toast.error('Preencha descrição e valor');
      return;
    }
    setSubmitting(true);
    try {
      const token = getToken();
      await axios.post(`${API}/admin/costs`, {
        ...form,
        amount: parseFloat(form.amount.toString().replace(',', '.')),
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Custo registrado!');
      setForm({ category: 'trafego_pago', description: '', amount: '', date: new Date().toISOString().slice(0, 10) });
      setShowForm(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao registrar custo');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReclassify = async (id, category) => {
    try {
      const token = getToken();
      await axios.patch(`${API}/admin/costs/${id}`, { category }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Categoria atualizada — CAC recalculado');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao reclassificar'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja remover este custo?')) return;
    try {
      const token = getToken();
      await axios.delete(`${API}/admin/costs/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Custo removido');
      fetchData();
    } catch { toast.error('Erro ao remover'); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-10 h-10 animate-spin text-[#C5A059]" /></div>;

  const total = summary?.total_invested || 0;
  const cac = summary?.cac;
  const mrr = summary?.mrr || 0;
  const payback = summary?.payback_months;
  const pagantes = summary?.active_paid_clients || 0;
  const cacPayback = summary?.cac_payback_months;
  const costPerClient = summary?.cost_per_client;
  const cacByMonth = summary?.cac_by_month || [];
  const aguardando = summary?.awaiting_first_payment || [];
  const mrrPrevisto = summary?.mrr_expected;
  const recebidoMes = summary?.received_this_month;
  const pararam = summary?.lapsed_clients || [];

  return (
    <div className="space-y-6" data-testid="admin-costs-tab">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <Wallet className="w-5 h-5 text-[#C5A059]" /> Custos do Projeto
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">Acompanhe quanto você investiu e quando o projeto começa a dar lucro</p>
        </div>
        <Button onClick={() => setShowForm(!showForm)} className="bg-[#0A2647] hover:bg-[#0e3260] text-white gap-2" data-testid="add-cost-btn">
          <Plus className="w-4 h-4" /> Registrar custo
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
        <div className="bg-gradient-to-br from-rose-500/10 to-rose-50 border border-rose-200 rounded-2xl p-5" data-testid="kpi-total-invested">
          <div className="flex items-center gap-2 mb-2">
            <TrendingDown className="w-4 h-4 text-rose-600" />
            <p className="text-xs font-bold uppercase tracking-wider text-rose-700">Total investido</p>
          </div>
          <p className="text-3xl font-extrabold text-rose-700">{formatBRL(total)}</p>
          <p className="text-[11px] text-rose-600/70 mt-1">{summary?.total_costs_count || 0} lançamento(s)</p>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="kpi-cac">
          <div className="flex items-center gap-2 mb-2">
            <Tag className="w-4 h-4 text-amber-600" />
            <p className="text-xs font-bold uppercase tracking-wider text-amber-700">CAC</p>
          </div>
          <p className="text-3xl font-extrabold text-slate-900">{cac != null ? formatBRL(cac) : '—'}</p>
          <p className="text-[11px] text-slate-500 mt-1">Tráfego + Marketing + Comissões ÷ {pagantes} pagante{pagantes === 1 ? '' : 's'} confirmado{pagantes === 1 ? '' : 's'}</p>
        </div>

        <div className="bg-emerald-50 rounded-2xl border border-emerald-200 p-5" data-testid="kpi-mrr">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className="w-4 h-4 text-emerald-600" />
            <p className="text-xs font-bold uppercase tracking-wider text-emerald-700">MRR atual</p>
          </div>
          <p className="text-3xl font-extrabold text-emerald-700">{formatBRL(mrr)}</p>
          <p className="text-[11px] text-emerald-600/70 mt-1">Só de quem já pagou · Previsto: {formatBRL(mrrPrevisto)}</p>
        </div>

        <div className="bg-[#0A2647] text-white rounded-2xl p-5" data-testid="kpi-payback">
          <div className="flex items-center gap-2 mb-2">
            <Calendar className="w-4 h-4 text-[#C5A059]" />
            <p className="text-xs font-bold uppercase tracking-wider text-[#C5A059]">Payback do CAC</p>
          </div>
          <p className="text-3xl font-extrabold text-white">
            {cacPayback != null ? cacPayback.toLocaleString('pt-BR', { maximumFractionDigits: 1 }) : '—'}
            {cacPayback != null ? <span className="text-base font-normal opacity-70 ml-1">{cacPayback === 1 ? 'mês' : 'meses'}</span> : null}
          </p>
          <p className="text-[11px] text-white/50 mt-1">meses de mensalidade para pagar a aquisição</p>
        </div>
        <div className="bg-teal-50 rounded-2xl border border-teal-200 p-5" data-testid="kpi-received-month">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className="w-4 h-4 text-teal-600" />
            <p className="text-xs font-bold uppercase tracking-wider text-teal-700">Recebido no mês</p>
          </div>
          <p className="text-3xl font-extrabold text-teal-700">{formatBRL(recebidoMes)}</p>
          <p className="text-[11px] text-teal-600/70 mt-1">pagamentos confirmados no Asaas neste mês</p>
        </div>
      </div>

      <p className="text-[11px] text-slate-500 -mt-2" data-testid="costs-secondary-line">
        Custo total por cliente: <strong>{costPerClient != null ? formatBRL(costPerClient) : '—'}</strong>
        {' · '}Retorno do investimento total: <strong>{payback != null ? formatMonths(payback) : '—'}</strong>
        {' · '}MRR previsto (confirmados + aguardando): <strong>{formatBRL(mrrPrevisto)}</strong>
        {aguardando.length > 0 && <> ({aguardando.length} aguardando 1º pagamento)</>}
      </p>

      {/* Aguardando 1º pagamento */}
      {aguardando.length > 0 && (
        <div>
          <h3 className="text-sm font-bold text-slate-700 mb-1">
            Aguardando 1º pagamento ({aguardando.length})
          </h3>
          <p className="text-[11px] text-slate-500 mb-3">
            Plano pago ativo e nenhum pagamento confirmado no Asaas até hoje (trial, CNPJ pendente, boleto a vencer ou 1º boleto em atraso).
            Somam {formatBRL(summary?.mrr_awaiting || 0)}/mês quando entrarem.
          </p>
          <Card className="border-0 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="awaiting-payment-table">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left p-3 text-[11px] font-bold text-slate-600 uppercase tracking-wider">Empresa</th>
                    <th className="text-left p-3 text-[11px] font-bold text-slate-600 uppercase tracking-wider">Plano</th>
                    <th className="text-right p-3 text-[11px] font-bold text-slate-600 uppercase tracking-wider">Mensalidade</th>
                    <th className="text-right p-3 text-[11px] font-bold text-slate-600 uppercase tracking-wider">1º boleto previsto</th>
                  </tr>
                </thead>
                <tbody>
                  {aguardando.map(a => (
                    <tr key={a.company_id} className="border-b border-slate-100 hover:bg-slate-50 transition" data-testid={`awaiting-row-${a.company_id}`}>
                      <td className="p-3 text-slate-700 font-medium">{a.name}</td>
                      <td className="p-3 text-slate-500">{a.plan}</td>
                      <td className="p-3 text-right text-slate-700">{formatBRL(a.monthly_value)}</td>
                      <td className="p-3 text-right text-slate-600">
                        {a.expected_first_charge ? a.expected_first_charge.split('-').reverse().join('/') : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}

      {/* Pararam de pagar */}
      {pararam.length > 0 && (
        <div>
          <h3 className="text-sm font-bold text-slate-700 mb-1">
            Pagaram antes e pararam ({pararam.length})
          </h3>
          <p className="text-[11px] text-slate-500 mb-3">
            Já tiveram pagamento confirmado, mas nada nos últimos {summary?.confirmed_window_days || 35} dias.
            Fora do MRR até voltarem a pagar ({formatBRL(summary?.mrr_lapsed || 0)}/mês em risco).
          </p>
          <Card className="border-0 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="lapsed-payment-table">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left p-3 text-[11px] font-bold text-slate-600 uppercase tracking-wider">Empresa</th>
                    <th className="text-left p-3 text-[11px] font-bold text-slate-600 uppercase tracking-wider">Plano</th>
                    <th className="text-right p-3 text-[11px] font-bold text-slate-600 uppercase tracking-wider">Mensalidade</th>
                    <th className="text-right p-3 text-[11px] font-bold text-slate-600 uppercase tracking-wider">Último pagamento</th>
                  </tr>
                </thead>
                <tbody>
                  {pararam.map(a => (
                    <tr key={a.company_id} className="border-b border-slate-100 hover:bg-slate-50 transition" data-testid={`lapsed-row-${a.company_id}`}>
                      <td className="p-3 text-slate-700 font-medium">{a.name}</td>
                      <td className="p-3 text-slate-500">{a.plan}</td>
                      <td className="p-3 text-right text-slate-700">{formatBRL(a.monthly_value)}</td>
                      <td className="p-3 text-right text-amber-700 font-semibold">
                        {a.last_paid_date ? a.last_paid_date.split('-').reverse().join('/') : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}

      {/* CAC por mês */}
      {cacByMonth.length > 0 && (
        <div>
          <h3 className="text-sm font-bold text-slate-700 mb-3">CAC por mês</h3>
          <Card className="border-0 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="cac-by-month-table">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left p-3 text-[11px] font-bold text-slate-600 uppercase tracking-wider">Mês</th>
                    <th className="text-right p-3 text-[11px] font-bold text-slate-600 uppercase tracking-wider">Gasto de aquisição</th>
                    <th className="text-right p-3 text-[11px] font-bold text-slate-600 uppercase tracking-wider">Novos pagantes</th>
                    <th className="text-right p-3 text-[11px] font-bold text-slate-600 uppercase tracking-wider">CAC</th>
                  </tr>
                </thead>
                <tbody>
                  {cacByMonth.map(r => (
                    <tr key={r.month} className="border-b border-slate-100 hover:bg-slate-50 transition" data-testid={`cac-row-${r.month}`}>
                      <td className="p-3 text-slate-600 whitespace-nowrap">{r.month.split('-').reverse().join('/')}</td>
                      <td className="p-3 text-right text-slate-700">{formatBRL(r.acquisition_total)}</td>
                      <td className="p-3 text-right font-semibold text-slate-700">{r.new_paid_clients}</td>
                      <td className="p-3 text-right font-bold text-amber-700">{r.cac != null ? formatBRL(r.cac) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}

      {/* Add Form */}
      {showForm && (
        <Card className="border-2 border-[#C5A059]/30 shadow-md" data-testid="cost-form">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-[#0A2647]">Novo lançamento</h3>
              <button onClick={() => setShowForm(false)} className="p-1 hover:bg-slate-100 rounded">
                <X className="w-4 h-4" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-12 gap-3">
              <div className="md:col-span-3">
                <Label className="text-xs font-medium text-slate-600">Categoria</Label>
                <select
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="mt-1 w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-sm"
                  data-testid="cost-category"
                >
                  {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>{v.icon} {v.label}</option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-4">
                <Label className="text-xs font-medium text-slate-600">Descrição</Label>
                <Input
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Ex: Google Ads - campanha Padarias"
                  className="mt-1 h-10 text-sm"
                  data-testid="cost-description"
                />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs font-medium text-slate-600">Valor (R$)</Label>
                <Input
                  type="text"
                  inputMode="decimal"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  placeholder="60,00"
                  className="mt-1 h-10 text-sm"
                  data-testid="cost-amount"
                />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs font-medium text-slate-600">Data</Label>
                <Input
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  className="mt-1 h-10 text-sm"
                  data-testid="cost-date"
                />
              </div>
              <div className="md:col-span-1 flex items-end">
                <Button type="submit" disabled={submitting} className="w-full h-10 bg-[#C5A059] hover:bg-[#b8933f] text-[#0A2647] font-bold" data-testid="cost-submit">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Salvar'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* By Category */}
      {summary?.by_category?.length > 0 && (
        <div>
          <h3 className="text-sm font-bold text-slate-700 mb-3">Distribuição por categoria</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {summary.by_category.map(cat => {
              const meta = CATEGORY_LABELS[cat.category] || CATEGORY_LABELS.outros;
              const pct = total > 0 ? Math.round((cat.total / total) * 100) : 0;
              return (
                <div key={cat.category} className="bg-white rounded-xl border border-slate-200 p-3">
                  <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${meta.color} mb-2`}>
                    <span>{meta.icon}</span><span>{meta.label}</span>
                  </div>
                  <p className="text-base font-bold text-slate-900">{formatBRL(cat.total)}</p>
                  <p className="text-[10px] text-slate-500">{pct}% do total</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* List */}
      <div>
        <h3 className="text-sm font-bold text-slate-700 mb-3">Histórico de lançamentos</h3>
        {costs.length === 0 ? (
          <Card className="border-dashed border-2 border-slate-200">
            <CardContent className="p-10 text-center">
              <AlertCircle className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-sm font-semibold text-slate-600">Nenhum custo registrado ainda</p>
              <p className="text-xs text-slate-400 mt-1">Clique em "Registrar custo" para começar</p>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-0 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="costs-table">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left p-3 text-[11px] font-bold text-slate-600 uppercase tracking-wider">Data</th>
                    <th className="text-left p-3 text-[11px] font-bold text-slate-600 uppercase tracking-wider">Categoria</th>
                    <th className="text-left p-3 text-[11px] font-bold text-slate-600 uppercase tracking-wider">Descrição</th>
                    <th className="text-right p-3 text-[11px] font-bold text-slate-600 uppercase tracking-wider">Valor</th>
                    <th className="w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {costs.map(c => {
                    const meta = CATEGORY_LABELS[c.category] || CATEGORY_LABELS.outros;
                    return (
                      <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50 transition" data-testid={`cost-row-${c.id}`}>
                        <td className="p-3 text-slate-600 whitespace-nowrap">{c.date.split('-').reverse().join('/')}</td>
                        <td className="p-3">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${meta.color}`}>
                            <span>{meta.icon}</span><span>{meta.label}</span>
                          </span>
                          <select
                            value={c.category}
                            onChange={(e) => handleReclassify(c.id, e.target.value)}
                            className="mt-1 block w-full max-w-[190px] h-7 px-2 border border-slate-200 rounded text-[11px] bg-white text-slate-600"
                            title="Reclassificar este lançamento (afeta o CAC)"
                            data-testid={`reclassify-cost-${c.id}`}
                          >
                            {Object.entries(CATEGORY_LABELS).map(([k, m]) => (
                              <option key={k} value={k}>{m.icon} {m.label}</option>
                            ))}
                          </select>
                        </td>
                        <td className="p-3 text-slate-700">{c.description}</td>
                        <td className="p-3 text-right font-bold text-rose-600">{formatBRL(c.amount)}</td>
                        <td className="p-3">
                          <button onClick={() => handleDelete(c.id)} className="p-1.5 hover:bg-rose-50 rounded text-rose-500 transition" data-testid={`delete-cost-${c.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>

      {/* Insights */}
      {total > 0 && (
        <Card className="border-2 border-[#C5A059]/30 bg-gradient-to-br from-[#FFF8E7] to-[#FFF1D6]">
          <CardContent className="p-5">
            <p className="text-[11px] font-bold text-[#C5A059] uppercase tracking-widest mb-2">💡 Análise</p>
            <p className="text-sm text-[#5C4014] leading-relaxed" data-testid="costs-analysis">
              Conquistar cada cliente custa <strong>{cac != null ? formatBRL(cac) : '—'}</strong> (gasto de aquisição de{' '}
              <strong>{formatBRL(summary?.acquisition_total || 0)}</strong> ÷ {pagantes} pagantes confirmados).
              {cacPayback != null && (
                <> Com ticket médio de <strong>{formatBRL(summary?.avg_ticket || 0)}</strong>, a aquisição se paga em{' '}
                  <strong>{formatMonths(cacPayback)}</strong> de mensalidade.
                  {cacPayback <= 3 && ' 🚀 Excelente: o cliente se paga rápido.'}
                  {cacPayback > 3 && cacPayback <= 6 && ' 📈 Saudável.'}
                  {cacPayback > 6 && ' ⚠️ Alto: reduza o custo de aquisição ou aumente o ticket.'}
                </>
              )}
              {payback != null && (
                <> Somando <strong>tudo</strong> que já foi investido no projeto ({formatBRL(total)}), o MRR de{' '}
                  <strong>{formatBRL(mrr)}/mês</strong> devolve o investimento em <strong>{formatMonths(payback)}</strong>.</>
              )}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdminCostsTab;
